/**
 * O erro que a UI sabe desenhar.
 *
 * A API .NET responde ProblemDetails (RFC 9457) com `codigo` e
 * `correlation_id` nas extensões. Este módulo traduz isso para uma forma
 * que a tela consegue usar sem adivinhar — inclusive para decidir se existe
 * caminho de reprocesso.
 */

/** Extensões que a API .NET põe no ProblemDetails. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  /** Código em inglês SCREAMING_SNAKE. É chave de log e de métrica. */
  codigo?: string;
  correlation_id?: string;
}

export type OrigemDoErro = 'rede' | 'http' | 'formato' | 'cancelado';

export class ErroDaApi extends Error {
  readonly origem: OrigemDoErro;
  readonly status: number | null;
  /** Código em inglês. `null` quando a resposta não trouxe ProblemDetails. */
  readonly codigo: string | null;
  /** Carregado, nunca regenerado. É o fio que liga a tela ao log do robô. */
  readonly correlationId: string | null;

  constructor(parametros: {
    mensagem: string;
    origem: OrigemDoErro;
    status?: number | null;
    codigo?: string | null;
    correlationId?: string | null;
  }) {
    super(parametros.mensagem);
    this.name = 'ErroDaApi';
    this.origem = parametros.origem;
    this.status = parametros.status ?? null;
    this.codigo = parametros.codigo ?? null;
    this.correlationId = parametros.correlationId ?? null;
  }

  /**
   * Se vale a pena tentar de novo.
   *
   * 4xx (menos 408 e 429) é erro de pedido: repetir dá o mesmo resultado e
   * só gasta o tempo de quem está olhando a fila.
   */
  get vaiAdiantarTentarDeNovo(): boolean {
    if (this.origem === 'rede') return true;
    if (this.origem === 'cancelado') return false;
    // Resposta que o front não entende — JSON quebrado ou corpo fora do
    // contrato — não muda por insistência: a mesma requisição devolve o
    // mesmo corpo inválido. Oferecer "tentar de novo" aqui é treinar a
    // pessoa a clicar em botão que não faz nada.
    if (this.origem === 'formato') return false;
    if (this.status === null) return true;
    if (this.status === 408 || this.status === 429) return true;
    return this.status >= 500;
  }
}

/** Mensagem em português — é o que a pessoa lê. */
export function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ErroDaApi) return erro.message;
  if (erro instanceof Error) return erro.message;
  return 'Ocorreu um erro inesperado.';
}

export function comoErroDaApi(erro: unknown): ErroDaApi {
  if (erro instanceof ErroDaApi) return erro;
  return new ErroDaApi({ mensagem: mensagemDeErro(erro), origem: 'formato' });
}
